import React, { useState, useEffect } from 'react';
import { Loader2, Save, Plus, Trash2, Phone, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const CompanyInfoPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { getAuthHeaders } = useAuth();
  const [formData, setFormData] = useState({
    company_name: '',
    tagline: '',
    description: '',
    mission: '',
    vision: '',
    addresses: [],
    email: '',
    phones: [],
    linkedin: '',
    twitter: '',
    instagram: '',
    youtube: ''
  });

  useEffect(() => {
    loadCompanyInfo();
  }, []);

  const loadCompanyInfo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/company-info`);
      const data = await response.json();
      // Ensure arrays exist
      setFormData({
        ...data,
        phones: data.phones || [],
        addresses: data.addresses || []
      });
    } catch (error) {
      toast.error('Failed to load company info');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Phone handlers
  const addPhone = () => {
    setFormData({
      ...formData,
      phones: [...formData.phones, { number: '', label: 'Main', country: '', is_primary: formData.phones.length === 0 }]
    });
  };

  const updatePhone = (index, field, value) => {
    const newPhones = [...formData.phones];
    newPhones[index] = { ...newPhones[index], [field]: value };
    
    // If setting as primary, unset others
    if (field === 'is_primary' && value) {
      newPhones.forEach((p, i) => {
        if (i !== index) p.is_primary = false;
      });
    }
    
    setFormData({ ...formData, phones: newPhones });
  };

  const removePhone = (index) => {
    const newPhones = formData.phones.filter((_, i) => i !== index);
    setFormData({ ...formData, phones: newPhones });
  };

  // Address handlers
  const addAddress = () => {
    setFormData({
      ...formData,
      addresses: [...formData.addresses, { address: '', label: 'Headquarters', country: '', is_primary: formData.addresses.length === 0 }]
    });
  };

  const updateAddress = (index, field, value) => {
    const newAddresses = [...formData.addresses];
    newAddresses[index] = { ...newAddresses[index], [field]: value };
    
    // If setting as primary, unset others
    if (field === 'is_primary' && value) {
      newAddresses.forEach((a, i) => {
        if (i !== index) a.is_primary = false;
      });
    }
    
    setFormData({ ...formData, addresses: newAddresses });
  };

  const removeAddress = (index) => {
    const newAddresses = formData.addresses.filter((_, i) => i !== index);
    setFormData({ ...formData, addresses: newAddresses });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/company-info`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save');

      toast.success('Company info updated successfully!');
    } catch (error) {
      toast.error('Failed to save company info');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-[#320547]" size={48} />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company Information</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Company Name</label>
              <Input
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Tagline</label>
              <Input
                name="tagline"
                value={formData.tagline}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <Textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Mission</label>
            <Textarea
              name="mission"
              value={formData.mission || ''}
              onChange={handleChange}
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Vision</label>
            <Textarea
              name="vision"
              value={formData.vision || ''}
              onChange={handleChange}
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          {/* Phone Numbers Section */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Phone size={20} className="text-[#320547]" />
                Phone Numbers
              </h3>
              <Button type="button" onClick={addPhone} variant="outline" size="sm">
                <Plus size={16} className="mr-1" /> Add Phone
              </Button>
            </div>
            
            {formData.phones.length === 0 ? (
              <p className="text-gray-500 text-sm">No phone numbers added. Click "Add Phone" to add one.</p>
            ) : (
              <div className="space-y-3">
                {formData.phones.map((phone, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Country</label>
                      <Input
                        value={phone.country || ''}
                        onChange={(e) => updatePhone(index, 'country', e.target.value)}
                        placeholder="e.g. India, USA"
                        className="w-full"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Phone Number</label>
                      <Input
                        value={phone.number}
                        onChange={(e) => updatePhone(index, 'number', e.target.value)}
                        placeholder="Phone number"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                      <select
                        value={phone.label}
                        onChange={(e) => updatePhone(index, 'label', e.target.value)}
                        className="px-3 py-2 border rounded-md text-sm h-10"
                      >
                        <option value="Main">Main</option>
                        <option value="Sales">Sales</option>
                        <option value="Support">Support</option>
                        <option value="HR">HR</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="pt-5">
                      <label className="flex items-center gap-1 text-sm whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={phone.is_primary}
                          onChange={(e) => updatePhone(index, 'is_primary', e.target.checked)}
                          className="w-4 h-4"
                        />
                        Primary
                      </label>
                    </div>
                    <div className="pt-5">
                      <Button
                        type="button"
                        onClick={() => removePhone(index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Addresses Section */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <MapPin size={20} className="text-[#320547]" />
                Addresses
              </h3>
              <Button type="button" onClick={addAddress} variant="outline" size="sm">
                <Plus size={16} className="mr-1" /> Add Address
              </Button>
            </div>
            
            {formData.addresses.length === 0 ? (
              <p className="text-gray-500 text-sm">No addresses added. Click "Add Address" to add one.</p>
            ) : (
              <div className="space-y-3">
                {formData.addresses.map((addr, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Country</label>
                        <Input
                          value={addr.country || ''}
                          onChange={(e) => updateAddress(index, 'country', e.target.value)}
                          placeholder="e.g. India, USA, UAE"
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Office Type</label>
                        <select
                          value={addr.label}
                          onChange={(e) => updateAddress(index, 'label', e.target.value)}
                          className="px-3 py-2 border rounded-md text-sm h-10"
                        >
                          <option value="Headquarters">Headquarters</option>
                          <option value="Branch Office">Branch Office</option>
                          <option value="Development Center">Development Center</option>
                          <option value="Sales Office">Sales Office</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="pt-5">
                        <label className="flex items-center gap-1 text-sm whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={addr.is_primary}
                            onChange={(e) => updateAddress(index, 'is_primary', e.target.checked)}
                            className="w-4 h-4"
                          />
                          Primary
                        </label>
                      </div>
                      <div className="pt-5">
                        <Button
                          type="button"
                          onClick={() => removeAddress(index)}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Full Address</label>
                      <Textarea
                        value={addr.address}
                        onChange={(e) => updateAddress(index, 'address', e.target.value)}
                        placeholder="Enter full address"
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Social Media Links</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">LinkedIn</label>
                <Input
                  name="linkedin"
                  value={formData.linkedin || ''}
                  onChange={handleChange}
                  placeholder="https://linkedin.com/company/..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Twitter</label>
                <Input
                  name="twitter"
                  value={formData.twitter || ''}
                  onChange={handleChange}
                  placeholder="https://twitter.com/..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Instagram</label>
                <Input
                  name="instagram"
                  value={formData.instagram || ''}
                  onChange={handleChange}
                  placeholder="https://instagram.com/..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">YouTube</label>
                <Input
                  name="youtube"
                  value={formData.youtube || ''}
                  onChange={handleChange}
                  placeholder="https://youtube.com/..."
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              className="bg-[#320547] hover:bg-[#95228A]"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={20} />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2" size={20} />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default CompanyInfoPanel;
